import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react'
import { Classroom as ClassroomConstants } from '@constants'
import { ClassroomManager, assertContext as assert } from '@helpers'

import { initialPlaybackState } from '../helpers'

const RecordingStateContext = createContext()
const RecordingDispatchContext = createContext()
const RecordingPlaybackContextState = createContext()
const RecordingPlaybackContextDispatch = createContext()
const RecordingPlaybackStateObserverContext = createContext()

const initialRecordingState = {
  error: null,
  step: ClassroomConstants.STEPS.FETCHING,
  panels: [],
  participants: {}
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return { ...initialRecordingState, step: state.step }
    case 'set_error':
      return { ...state, error: action.payload }
    case 'set_step':
      return { ...state, step: action.payload }
    case 'set_panels':
      return { ...state, panels: action.payload }
    case 'set_participants':
      return { ...state, participants: action.payload }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

export const RecordingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialRecordingState)
  const [playback, setPlayback] = useState(initialPlaybackState)
  const playbackStateObserverRef = useRef(null)

  return (
    <RecordingStateContext.Provider value={state}>
      <RecordingDispatchContext.Provider value={dispatch}>
        <RecordingPlaybackContextState.Provider value={playback}>
          <RecordingPlaybackContextDispatch.Provider value={setPlayback}>
            <RecordingPlaybackStateObserverContext.Provider
              value={playbackStateObserverRef}
            >
              {children}
            </RecordingPlaybackStateObserverContext.Provider>
          </RecordingPlaybackContextDispatch.Provider>
        </RecordingPlaybackContextState.Provider>
      </RecordingDispatchContext.Provider>
    </RecordingStateContext.Provider>
  )
}

export const useRecordingState = () => {
  const context = useContext(RecordingStateContext)
  assert(context, 'useRecordingState', 'RecordingStateProvider')
  return context
}

export const useRecordingDispatch = () => {
  const context = useContext(RecordingDispatchContext)
  assert(context, 'useRecordingDispatch', 'RecordingDispatchContext')

  return context
}

export const useRecordingActions = () => {
  const dispatch = useRecordingDispatch()

  const setStep = useCallback(
    (step) =>
      dispatch({
        type: 'set_step',
        payload: step
      }),
    [dispatch]
  )

  const setPanels = useCallback(
    (panels) =>
      dispatch({
        type: 'set_panels',
        payload: panels
      }),
    [dispatch]
  )

  const onPanelsChange = useCallback(() => {
    setPanels(ClassroomManager.getPanels())
    ClassroomManager.updatePanels()
  }, [setPanels])

  const onParticipantsChange = useCallback(() => {
    dispatch({
      type: 'set_participants',
      payload: ClassroomManager.participants.toJSON()
    })
  }, [dispatch])

  const reset = useCallback(() => {
    dispatch({
      type: 'reset'
    })
  }, [dispatch])

  return useMemo(() => {
    return { onPanelsChange, onParticipantsChange, setStep, setPanels, reset }
  }, [onPanelsChange, onParticipantsChange, reset, setPanels, setStep])
}

export const useRecording = () => {
  return [useRecordingState(), useRecordingDispatch()]
}

export const useRecordingPlayback = () => {
  const contexts = [
    useContext(RecordingPlaybackContextState),
    useContext(RecordingPlaybackContextDispatch)
  ]
  assert(contexts[0], 'useRecordingPlayback', 'RecordingPlaybackContextState')
  assert(
    contexts[1],
    'useRecordingPlayback',
    'RecordingPlaybackContextDispatch'
  )
  return contexts
}

export const useRecordingPlaybackState = () => {
  const context = useContext(RecordingPlaybackContextState)
  assert(context, 'useRecordingPlaybackState', 'RecordingPlaybackContextState')
  return context
}

export const useRecordingPlaybackDispatch = () => {
  const context = useContext(RecordingPlaybackContextDispatch)
  assert(
    context,
    'useRecordingPlaybackDispatch',
    'RecordingPlaybackContextDispatch'
  )
  return context
}

export const useRecordingPlaybackStateObserver = () => {
  const context = useContext(RecordingPlaybackStateObserverContext)
  assert(
    context,
    'useRecordingPlaybackStateObserver',
    'RecordingPlaybackStateObserverContext'
  )
  return context
}
